/**
 * Type definitions for Profile Modules
 * These types match the backend domain models and API DTOs
 */

import type {
  ProfileModuleType,
  ProfileTemplateFieldMode,
} from '@up2stay/up2promo-api-client';

/** Template-only: per-entry governance (stripped when syncing to profiles). */
export type ModuleEntryPolicy = ProfileTemplateFieldMode;

/**
 * Base interface for all module configurations
 */
export interface BaseModuleConfig {
  type: ProfileModuleType;
}

/**
 * Biography Module Configuration
 */
export interface BiographyModuleConfig extends BaseModuleConfig {
  type: ProfileModuleType.BIOGRAPHY;
  content: string;
  format?: 'plain' | 'markdown' | 'html';
  showReadMore?: boolean;
  maxLength?: number;
  policy?: ModuleEntryPolicy;
}

/**
 * Contact Info Module Configuration
 */
export enum ContactType {
  PRIVATE = 'private',
  BUSINESS = 'business',
}

export interface ContactInfoItem {
  label?: string;
  value: string;
  type?: ContactType;
  visible?: boolean;
}

/** Unified contact entry (email or phone) for free ordering, e.g. business email + business phone together. */
export interface ContactInfoEntry extends ContactInfoItem {
  id?: string;
  kind: 'email' | 'phone';
  policy?: ModuleEntryPolicy;
}

export interface ContactInfoModuleConfig extends BaseModuleConfig {
  type: ProfileModuleType.CONTACT_INFO;
  /** Unified list of email and phone entries in display order. */
  items?: ContactInfoEntry[];
  address?: string;
  website?: string;
  showPrivateData?: boolean;
  showBusinessData?: boolean;
}

/**
 * Socials Module Configuration
 */
export enum SocialPlatform {
  INSTAGRAM = 'instagram',
  LINKEDIN = 'linkedin',
  TWITTER = 'twitter',
  X = 'x',
  FACEBOOK = 'facebook',
  TIKTOK = 'tiktok',
  YOUTUBE = 'youtube',
  GITHUB = 'github',
  BEHANCE = 'behance',
  DRIBBBLE = 'dribbble',
  PINTEREST = 'pinterest',
  SNAPCHAT = 'snapchat',
  TWITCH = 'twitch',
  CUSTOM = 'custom',
}

export interface SocialLink {
  id?: string;
  platform: SocialPlatform;
  url: string;
  label?: string;
  icon?: string;
  visible?: boolean;
  policy?: ModuleEntryPolicy;
}

export interface SocialsModuleConfig extends BaseModuleConfig {
  type: ProfileModuleType.SOCIALS;
  links: SocialLink[];
  layout?: 'grid' | 'list' | 'minimal';
}

/**
 * Links Module Configuration
 */
export interface LinkItem {
  id?: string;
  title: string;
  url: string;
  description?: string;
  icon?: string;
  openInNewTab?: boolean;
  order?: number;
  visible?: boolean;
  policy?: ModuleEntryPolicy;
}

export interface LinksModuleConfig extends BaseModuleConfig {
  type: ProfileModuleType.LINKS;
  items: LinkItem[];
  layout?: 'grid' | 'list';
}

/**
 * Video Module Configuration (YouTube only, minimal).
 */
export interface VideoModuleConfig extends BaseModuleConfig {
  type: ProfileModuleType.VIDEO;
  url: string;
  title?: string;
  description?: string;
  policy?: ModuleEntryPolicy;
}

/**
 * Images Module Configuration
 */
export interface ImageItem {
  id?: string;
  policy?: ModuleEntryPolicy;
  file: {
    id: string;
    path: string;
    [key: string]: unknown;
  };
  title?: string;
  description?: string;
  order?: number;
  visible?: boolean;
}

/** Preset aspect ratio for the images module (applies to all images). */
export type ImagesAspectRatio = '1:1' | '4:3' | '16:9' | '3:2' | '3:4';

export interface ImagesModuleConfig extends BaseModuleConfig {
  type: ProfileModuleType.IMAGES;
  images: ImageItem[];
  aspectRatio?: ImagesAspectRatio;
  policy?: ModuleEntryPolicy;
}

/**
 * Files Module Configuration
 */
export interface FileItem {
  id?: string;
  policy?: ModuleEntryPolicy;
  file: {
    id: string;
    path: string;
    [key: string]: unknown;
  };
  title?: string;
  description?: string;
  category?: string;
  order?: number;
  visible?: boolean;
}

export interface FilesModuleConfig extends BaseModuleConfig {
  type: ProfileModuleType.FILES;
  files: FileItem[];
  policy?: ModuleEntryPolicy;
  /** @deprecated Only list layout is used. Kept for backward compat. */
  layout?: 'list' | 'grid' | 'compact';
  showDescriptions?: boolean;
  showFileSize?: boolean;
}

/**
 * Google Maps Module Configuration
 * Persist `locationId` only. Address/coords/title are resolved from the live
 * workspace location at preview and public profile read time.
 */
export interface GoogleMapsModuleConfig extends BaseModuleConfig {
  type: ProfileModuleType.GOOGLE_MAPS;
  policy?: ModuleEntryPolicy;
  /** Workspace location to display. Required for content when linking a location. */
  locationId?: string;
  /** Display overlay (resolved from location; not the persist contract when locationId is set). */
  address: string;
  latitude?: number;
  longitude?: number;
  zoom?: number;
  mapType?: 'roadmap' | 'satellite' | 'hybrid' | 'terrain';
  showMarker?: boolean;
  markerTitle?: string;
  markerDescription?: string;
  height?: number;
  allowFullscreen?: boolean;
}

/**
 * Team Members Module Configuration
 */
export interface TeamMember {
  id?: string;
  policy?: ModuleEntryPolicy;
  /** User ID for building profile link when profileUrl is not set. */
  userId?: string;
  /** Username for building profile link in workspace context. */
  username?: string;
  name: string;
  role?: string;
  title?: string;
  avatar?: {
    id: string;
    path: string;
    [key: string]: unknown;
  };
  bio?: string;
  email?: string;
  phone?: string;
  profileUrl?: string;
  order?: number;
  visible?: boolean;
}

export interface TeamMembersModuleConfig extends BaseModuleConfig {
  type: ProfileModuleType.TEAM_MEMBERS;
  members: TeamMember[];
  policy?: ModuleEntryPolicy;
  layout?: 'grid' | 'list' | 'carousel';
  columns?: number;
  showRoles?: boolean;
  showBios?: boolean;
  showContactInfo?: boolean;
  showProfileLinks?: boolean;
}

/**
 * Union type of all module configs
 */
export type ModuleConfig =
  | BiographyModuleConfig
  | ContactInfoModuleConfig
  | SocialsModuleConfig
  | LinksModuleConfig
  | VideoModuleConfig
  | ImagesModuleConfig
  | FilesModuleConfig
  | GoogleMapsModuleConfig
  | TeamMembersModuleConfig;
